import React, { useEffect, useState } from "react";
import { PlayCircle, Youtube } from "lucide-react";
import { getYoutubeEmbed } from "../lib/api";
import { optimizeImageUrl, HERO_PAGE_WIDTH } from "../lib/images";
import { resolveMediaUrl } from "../lib/mediaUrl";
import PlayerLoadingOverlay from "./PlayerLoadingOverlay";

export default function VideoPlayer({ video, autoplay = false, className = "" }) {
  const [loading, setLoading] = useState(true);

  const embed = video?.youtubeUrl ? getYoutubeEmbed(video.youtubeUrl, autoplay) : null;
  const fileSrc = video?.videoFileUrl ? resolveMediaUrl(video.videoFileUrl) : null;
  const hasPlayer = Boolean(fileSrc || embed);

  useEffect(() => {
    if (!hasPlayer) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timeout = window.setTimeout(() => setLoading(false), 12000);
    return () => window.clearTimeout(timeout);
  }, [video?.id, fileSrc, embed, hasPlayer]);

  if (!video) return null;

  const handleReady = () => {
    window.setTimeout(() => setLoading(false), 300);
  };

  if (fileSrc) {
    return (
      <div className="relative w-full h-full bg-black">
        <PlayerLoadingOverlay
          loading={loading}
          label="Video yükleniyor..."
          sublabel={video.title}
          coverImage={video.coverImage}
          variant="video"
        />
        <video
          src={fileSrc}
          controls
          autoPlay={autoplay}
          playsInline
          onLoadedData={handleReady}
          onCanPlay={handleReady}
          className={`w-full h-full bg-black transition-opacity duration-500 ${className} ${
            loading ? "opacity-0" : "opacity-100"
          }`}
        />
      </div>
    );
  }

  if (embed) {
    return (
      <div className="relative w-full h-full bg-[#0a0b16]">
        <PlayerLoadingOverlay
          loading={loading}
          label="Video yükleniyor..."
          sublabel={video.title}
          coverImage={video.coverImage}
          variant="video"
        />
        <iframe
          src={embed}
          title={video.title}
          onLoad={handleReady}
          className={`w-full h-full transition-opacity duration-500 ${className} ${
            loading ? "opacity-0" : "opacity-100"
          }`}
          allow="accelerated-download; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full relative flex flex-col items-center justify-center text-center bg-black">
      {video.coverImage && (
        <img
          src={optimizeImageUrl(video.coverImage, HERO_PAGE_WIDTH)}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
      )}
      <div className="relative z-10 flex flex-col items-center px-6">
        <div className="w-16 h-16 rounded-full bg-[rgba(139,92,246,0.18)] border border-[rgba(168,85,247,0.4)] flex items-center justify-center mb-4">
          <PlayCircle size={34} className="text-[var(--tv-purple-bright)]" />
        </div>
        <p className="text-white font-semibold text-lg">Video henüz eklenmedi</p>
        <p className="text-[var(--tv-muted)] text-sm mt-2 flex items-center gap-1.5">
          <Youtube size={15} /> Admin panelinden video veya YouTube bağlantısı ekleyin.
        </p>
      </div>
    </div>
  );
}
